import React from 'react';

import { EntertainmentPlayerName } from '@/components/entertainment/EntertainmentPlayerName';
import { Button, Card } from '@/components/ui';
import type { SharedCostDraft } from '@/lib/entertainment/ledger-types';
import type { EntertainmentCategory, User } from '@/lib/types';

type Props = {
  categories: EntertainmentCategory[];
  eligibleUsers: User[];
  currentUserId: string;
  defaultCategoryId?: string | null;
  canChoosePayer?: boolean;
  onReview: (draft: SharedCostDraft) => void;
};

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function centsFromDollars(value: string): number {
  return Math.round(Number(value) * 100);
}

function clampWeight(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, Math.round(value * 100) / 100));
}

function initialWeights(users: User[], currentUserId: string): Record<string, number> {
  return Object.fromEntries(users.map((user) => [user.id, user.id === currentUserId ? 1 : 0]));
}

function categoryWeights(
  users: User[],
  currentUserId: string,
  category?: EntertainmentCategory | null,
): Record<string, number> {
  if (!category) return initialWeights(users, currentUserId);
  return Object.fromEntries(
    users.map((user) => [user.id, clampWeight(Number(category.default_weights?.[user.id] ?? 0))]),
  );
}

export function SharedCostForm({
  categories,
  eligibleUsers,
  currentUserId,
  defaultCategoryId,
  canChoosePayer = false,
  onReview,
}: Props) {
  const firstCategoryId = defaultCategoryId ?? categories[0]?.id ?? '';
  const [amount, setAmount] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [categoryId, setCategoryId] = React.useState(firstCategoryId);
  const [payerId, setPayerId] = React.useState(currentUserId);
  const [transactionDate, setTransactionDate] = React.useState(todayIsoDate());
  const [weights, setWeights] = React.useState<Record<string, number>>(() => (
    categoryWeights(eligibleUsers, currentUserId, categories.find((category) => category.id === firstCategoryId))
  ));
  const [weightsExpanded, setWeightsExpanded] = React.useState(false);

  React.useEffect(() => {
    setWeights(categoryWeights(eligibleUsers, currentUserId, categories.find((category) => category.id === categoryId)));
  }, [categories, categoryId, eligibleUsers, currentUserId]);

  const updateWeight = (userId: string, nextValue: number) => {
    setWeights((previous) => ({
      ...previous,
      [userId]: clampWeight(nextValue),
    }));
  };

  const submitDraft = (approveImmediately: boolean) => {
    const amountCents = centsFromDollars(amount);
    if (!Number.isInteger(amountCents) || amountCents <= 0 || !description.trim() || !categoryId) return;

    onReview({
      amount_cents: amountCents,
      description: description.trim(),
      category_id: categoryId,
      payer_id: canChoosePayer ? payerId : currentUserId,
      transaction_date: transactionDate,
      weights,
      approve_immediately: canChoosePayer && approveImmediately,
    });
  };

  const submitReview = (event: React.FormEvent) => {
    event.preventDefault();
    submitDraft(false);
  };

  return (
    <Card className="p-3 sm:p-4">
      <form data-testid="shared-cost-form" className="flex flex-col gap-2.5" onSubmit={submitReview}>
        <div
          data-testid="shared-cost-row-amount-date"
          className="grid grid-cols-[auto_minmax(3.75rem,4.75rem)_auto_minmax(7rem,7.25rem)] items-center gap-x-1.5 gap-y-2 sm:grid-cols-[auto_minmax(7rem,10rem)_auto_minmax(7rem,10rem)]"
        >
          <label htmlFor="shared-cost-amount" className="text-sm font-semibold text-text-main">
            Amount:
          </label>
          <input
            id="shared-cost-amount"
            name="shared-cost-amount"
            autoComplete="off"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="h-9 w-full min-w-0 rounded-lg border border-border-subtle bg-surface px-1.5 text-sm text-text-main focus:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30 sm:px-3"
          />
          <label htmlFor="shared-cost-date" className="text-sm font-semibold text-text-main">
            Date:
          </label>
          <input
            id="shared-cost-date"
            name="shared-cost-date"
            autoComplete="off"
            type="date"
            value={transactionDate}
            onChange={(event) => setTransactionDate(event.target.value)}
            className="h-9 w-full min-w-0 rounded-lg border border-border-subtle bg-surface px-1.5 text-sm text-text-main focus:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30 sm:px-3"
          />
        </div>

        <label
          data-testid="shared-cost-row-description-label"
          htmlFor="shared-cost-description"
          className="text-sm font-semibold text-text-main"
        >
          Description
        </label>
        <div data-testid="shared-cost-row-description-field">
          <input
            id="shared-cost-description"
            name="shared-cost-description"
            aria-label="Description"
            autoComplete="off"
            type="text"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="h-9 w-full rounded-lg border border-border-subtle bg-surface px-3 text-sm text-text-main focus:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30"
          />
        </div>

        <div
          data-testid="shared-cost-row-category-payer"
          className="grid grid-cols-[auto_minmax(4rem,5.75rem)_auto_minmax(4rem,5.75rem)] items-center gap-x-1 gap-y-2 sm:grid-cols-[auto_minmax(7rem,10rem)_auto_minmax(7rem,10rem)]"
        >
          <label htmlFor="shared-cost-category" className="text-sm font-semibold text-text-main">
            Category:
          </label>
          <select
            id="shared-cost-category"
            name="shared-cost-category"
            autoComplete="off"
            value={categoryId}
            onChange={(event) => {
              const nextCategoryId = event.target.value;
              setCategoryId(nextCategoryId);
              setWeights(categoryWeights(eligibleUsers, currentUserId, categories.find((item) => item.id === nextCategoryId)));
            }}
            className="h-9 w-full min-w-0 rounded-lg border border-border-subtle bg-surface px-1.5 text-sm text-text-main focus:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30 sm:px-3"
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
          <label htmlFor="shared-cost-payer" className="text-sm font-semibold text-text-main">
            Payer:
          </label>
          <select
            id="shared-cost-payer"
            name="shared-cost-payer"
            autoComplete="off"
            value={canChoosePayer ? payerId : currentUserId}
            disabled={!canChoosePayer}
            onChange={(event) => setPayerId(event.target.value)}
            className="h-9 w-full min-w-0 rounded-lg border border-border-subtle bg-surface px-1.5 text-sm text-text-main focus:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30 disabled:opacity-70 sm:px-3"
          >
            {eligibleUsers.map((user) => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1 pt-1">
          <button
            type="button"
            onClick={() => setWeightsExpanded((prev) => !prev)}
            className="flex w-full items-center justify-between rounded-xl bg-aura-primary/[0.04] border border-aura-primary/10 px-3 py-2 text-[13px] font-bold uppercase tracking-[0.1em] text-aura-primary transition-colors hover:bg-aura-primary/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aura-primary/30"
            aria-expanded={weightsExpanded}
            aria-controls="shared-cost-weight-list"
          >
            <span>Weights List</span>
            <span className={`transition-transform duration-300 ${weightsExpanded ? 'rotate-180' : ''}`} aria-hidden="true">▼</span>
          </button>
          <div
            id="shared-cost-weight-list"
            data-testid="shared-cost-weight-list"
            className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
              weightsExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="flex flex-col gap-1 pt-2">
              {eligibleUsers.map((user) => {
                const weight = weights[user.id] ?? 0;
                return (
                  <div
                    key={user.id}
                    data-testid={`shared-cost-weight-row-${user.id}`}
                    className="grid grid-cols-[minmax(0,1fr)_auto_5rem_auto] items-center gap-2 py-1"
                  >
                    <EntertainmentPlayerName user={user} />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 min-h-[32px] w-8 rounded-md p-0 px-0 py-0 text-sm"
                      aria-label={`Decrease ${user.name}`}
                      onClick={() => updateWeight(user.id, weight - 0.25)}
                    >
                      -
                    </Button>
                    <input
                      aria-label={`${user.name} weight`}
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={weight}
                      onChange={(event) => updateWeight(user.id, Number(event.target.value))}
                      className="h-8 w-20 rounded-lg border border-border-subtle bg-surface px-2 text-center text-sm text-text-main focus:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 min-h-[32px] w-8 rounded-md p-0 px-0 py-0 text-sm"
                      aria-label={`Increase ${user.name}`}
                      onClick={() => updateWeight(user.id, weight + 0.25)}
                    >
                      +
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div data-testid="shared-cost-action-row" className="flex flex-row justify-center gap-2 pt-1">
          <Button
            type="submit"
            size="sm"
            className="min-w-0 flex-1 whitespace-nowrap px-2 text-[11px] text-white sm:flex-none sm:px-4 sm:text-[13px]"
            aria-label="Review Shared Cost"
          >
            Review Shared Cost
          </Button>
          {canChoosePayer && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="min-w-0 flex-1 whitespace-nowrap px-2 text-[11px] text-aura-primary sm:flex-none sm:px-4 sm:text-[13px]"
              aria-label="Approve Immediately"
              onClick={() => submitDraft(true)}
            >
              Approve Immediately
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
}
